/*
 * Cryptoart mobile menu drawer: inline accordion behaviour.
 *
 * Dawn's MenuDrawer (global.js) treats every nested <details> as a slide-in
 * sub-panel: it adds .menu-opening / .submenu-open, traps focus inside the
 * panel and only closes it through a back button. This script intercepts
 * those clicks first (capture phase) so a group toggles in place instead:
 *   - tap the row to expand, tap again to collapse (grid-rows animation)
 *   - the open/closed state is remembered for the tab (sessionStorage) and
 *     restored every time the drawer opens, since Dawn resets nested
 *     <details> on close and every Shopify navigation is a full page load
 *   - the X inside the drawer closes it through Dawn's own closeMenuDrawer
 * Markup: snippets/header-drawer.liquid. Styles: component-menu-drawer-accordion.css
 */
(() => {
  const STORAGE_PREFIX = 'cryptoart:menu-drawer:';
  const ACCORDION = '.menu-drawer__accordion';
  const PANEL = '.menu-drawer__submenu--inline';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const remember = (details, open) => {
    try {
      sessionStorage.setItem(STORAGE_PREFIX + details.id, open ? '1' : '0');
    } catch (error) {
      /* storage unavailable (private mode etc.): state just resets per page */
    }
  };

  const recall = (details) => {
    try {
      return sessionStorage.getItem(STORAGE_PREFIX + details.id) === '1';
    } catch (error) {
      return false;
    }
  };

  const summaryOf = (details) => details.querySelector(':scope > summary');

  const expand = (details, animate) => {
    details.open = true;
    summaryOf(details).setAttribute('aria-expanded', 'true');
    if (animate && !reducedMotion.matches) {
      // two frames so the panel is laid out at 0fr before transitioning to 1fr
      requestAnimationFrame(() => requestAnimationFrame(() => details.classList.add('is-open')));
    } else {
      details.classList.add('is-open');
    }
  };

  const collapse = (details, animate) => {
    const panel = details.querySelector(`:scope > ${PANEL}`);
    summaryOf(details).setAttribute('aria-expanded', 'false');
    details.classList.remove('is-open');
    if (!animate || reducedMotion.matches || !panel) {
      details.open = false;
      return;
    }
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      panel.removeEventListener('transitionend', onEnd);
      if (!details.classList.contains('is-open')) details.open = false;
    };
    const onEnd = (event) => {
      if (event.target === panel) finish();
    };
    panel.addEventListener('transitionend', onEnd);
    window.setTimeout(finish, 350); // safety net if transitionend never fires
  };

  const init = () => {
    const drawer = document.querySelector('header-drawer');
    const main = drawer && drawer.querySelector('#Details-menu-drawer-container');
    const mainSummary = main && summaryOf(main);
    if (!drawer || !main || !mainSummary) return;

    // Accordion rows and the drawer's own X. Capture phase + stopPropagation
    // keeps Dawn's onSummaryClick / onCloseButtonClick from running for these.
    drawer.addEventListener(
      'click',
      (event) => {
        const summary = event.target.closest(`${ACCORDION} > summary`);
        if (summary && drawer.contains(summary)) {
          event.preventDefault();
          event.stopPropagation();
          const details = summary.parentElement;
          const open = !details.classList.contains('is-open');
          open ? expand(details, true) : collapse(details, true);
          remember(details, open);
          return;
        }
        const dismiss = event.target.closest('.menu-drawer__dismiss');
        if (dismiss && drawer.contains(dismiss)) {
          event.preventDefault();
          event.stopPropagation();
          if (typeof drawer.closeMenuDrawer === 'function') {
            drawer.closeMenuDrawer(event, mainSummary);
            mainSummary.setAttribute('aria-expanded', 'false');
          } else {
            main.open = false;
          }
        }
      },
      true
    );

    // Escape closes the whole drawer (Dawn would only close the group).
    drawer.addEventListener(
      'keyup',
      (event) => {
        if (event.code.toUpperCase() !== 'ESCAPE' || !main.open) return;
        if (!event.target.closest(ACCORDION)) return; // let Dawn handle the plain case
        event.stopPropagation();
        if (typeof drawer.closeMenuDrawer === 'function') drawer.closeMenuDrawer(event, mainSummary);
      },
      true
    );

    // Dawn strips [open] from every nested <details> when the drawer closes;
    // put the remembered groups back each time it opens.
    main.addEventListener('toggle', () => {
      if (!main.open) return;
      main.querySelectorAll(ACCORDION).forEach((details) => {
        recall(details) ? expand(details, false) : collapse(details, false);
      });
    });

    // Keep class/aria in sync if something else (Dawn's close) removes [open].
    main.querySelectorAll(ACCORDION).forEach((details) => {
      details.addEventListener('toggle', () => {
        if (details.open) return;
        details.classList.remove('is-open');
        summaryOf(details).setAttribute('aria-expanded', 'false');
      });
    });
  };

  const start = () => {
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('header-drawer').then(init, init);
    } else {
      init();
    }
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
})();
